/**
 * Fetch Liked Videos Page UseCase
 *
 * Pages through the locally-stored liked-video records, fetches the matching
 * media documents from Firestore, applies blocked / hidden / usersBlockedMe
 * filtering, and preserves the original path order.
 *
 * Mirrors `fetchUnlockedVideosPageUseCase` 1:1 — the only difference is the
 * local-list element type (`UserMediaLikeStorage` instead of
 * `UnlockedContentStorage`). The `liked === true` filter is applied
 * upstream by `likedVideosService.listLocalLikedVideos` (Task 1), so this
 * use case never inspects the `liked` flag.
 *
 * Flutter parity:
 *  - lib/presentation/screens/content/liked_content_screen.dart:115-160
 *    (pagination shape, re-ordering against `valuesToSearch`,
 *    `hasMore = _medias.length < _userMediaLikes.length`)
 *  - lib/data/data_sources/content_data_source.dart:261-299 / 362-400
 *    (post-query filters: blocked, usersBlockedMe, hiddenContent — same
 *    as getUnlockedContent)
 */

import { userMediaService } from '@/domain/services/userMedia/userMediaService';
import { shouldFilterOutBlockedOrHiddenMediaUseCase } from '@/domain/usecases/userMedia/shouldFilterOutBlockedOrHiddenMediaUseCase';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import type { UserMediaLikeStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export interface FetchLikedVideosPageParams {
  localItems: UserMediaLikeStorage[];
  pageIndex: number;
  pageSize?: number;
  currentUser: UserEntity | null;
}

export interface FetchLikedVideosPageResult {
  medias: UserMediaEntity[];
  hasMore: boolean;
}

export class FetchLikedVideosPageUseCase {
  /**
   * Execute one page fetch.
   *
   * Steps (mirroring Flutter `liked_content_screen.dart:_getData`):
   *  1. Slice `localItems` by [pageIndex * pageSize, pageIndex * pageSize + pageSize).
   *  2. Fetch the matching media documents (Firestore `in` query — order is not
   *     guaranteed by Firestore).
   *  3. Filter out blocked/hidden/usersBlockedMe medias (parity with
   *     content_data_source.dart:375-393 — same post-query guards as
   *     getUnlockedContent).
   *  4. Re-order the surviving medias by iterating `pathsInSlice` outer, then
   *     matching `media.relativePath === path` inner — required for parity
   *     with liked_content_screen.dart:148-154, since Firestore `in`
   *     queries don't honour input order.
   *  5. Compute `hasMore` from the slice bounds against the local list length
   *     (Flutter line 156).
   *
   * Errors from the service are not caught here — the hook layer is
   * responsible for logging via appLogger.error.
   */
  async execute(
    params: FetchLikedVideosPageParams,
  ): Promise<FetchLikedVideosPageResult> {
    const {
      localItems,
      pageIndex,
      pageSize = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT,
      currentUser,
    } = params;

    const start = pageIndex * pageSize;
    if (start >= localItems.length) {
      return { medias: [], hasMore: false };
    }

    const end = Math.min(start + pageSize, localItems.length);
    const pathsInSlice = localItems.slice(start, end).map((item) => item.path);

    if (pathsInSlice.length === 0) {
      return { medias: [], hasMore: false };
    }

    const rawMedias = await userMediaService.getMediasByPaths(pathsInSlice);

    const surviving = rawMedias.filter(
      (media) => !shouldFilterOutBlockedOrHiddenMediaUseCase.execute(media, currentUser),
    );

    // Re-order surviving medias to match `pathsInSlice` order — Flutter
    // liked_content_screen.dart:148-154.
    const ordered: UserMediaEntity[] = [];
    for (const path of pathsInSlice) {
      const match = surviving.find((media) => media.relativePath === path);
      if (match) {
        ordered.push(match);
      }
    }

    const hasMore = end < localItems.length;

    return { medias: ordered, hasMore };
  }
}

export const fetchLikedVideosPageUseCase = new FetchLikedVideosPageUseCase();
